import { Box, Rating, Typography } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import { useTranslation } from "react-i18next";
import {
  colorBorder,
  colorSurface,
  colorTextMuted,
  colorTextSecondary,
  radiusCard,
  shadowCard,
} from "src/design/tokens";
import { PublicRestaurantInfo } from "src/data/clientBooking/queries/clientBooking";

interface Props {
  restaurant: PublicRestaurantInfo;
}

const RestaurantHeader = ({ restaurant }: Props) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        borderRadius: radiusCard,
        overflow: "hidden",
        backgroundColor: colorSurface,
        border: `1px solid ${colorBorder}`,
        boxShadow: shadowCard,
        mb: 3,
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: { xs: 180, sm: 220 },
          overflow: "hidden",
        }}
      >
        {restaurant.coverUrl ? (
          <img
            src={restaurant.coverUrl}
            alt={restaurant.name}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              backgroundColor: colorBorder,
            }}
          />
        )}
      </Box>

      <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
          {restaurant.name}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <Rating
            value={restaurant.googleRating}
            precision={0.1}
            readOnly
            size="small"
          />
          <Typography variant="body2" color={colorTextMuted}>
            {restaurant.googleRating.toFixed(1)}{" "}
            {restaurant.reviewCount
              ? `(${t("clientBooking.reviews", { count: restaurant.reviewCount })})`
              : ""}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LocationOnIcon
              sx={{ fontSize: 16, color: colorTextMuted, flexShrink: 0 }}
            />
            <Typography variant="body2" color={colorTextSecondary}>
              {restaurant.address}
            </Typography>
          </Box>

          {restaurant.openingDisplay && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AccessTimeIcon
                sx={{ fontSize: 16, color: colorTextMuted, flexShrink: 0 }}
              />
              <Typography variant="body2" color={colorTextSecondary}>
                {t("clientBooking.openHours", {
                  hours: restaurant.openingDisplay,
                })}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PhoneIcon
              sx={{ fontSize: 16, color: colorTextMuted, flexShrink: 0 }}
            />
            <Typography variant="body2" color={colorTextSecondary}>
              {restaurant.phone}
            </Typography>
          </Box>
        </Box>

        {restaurant.description && (
          <Typography
            variant="body2"
            color={colorTextMuted}
            sx={{ mt: 1.5, lineHeight: 1.6 }}
          >
            {restaurant.description}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default RestaurantHeader;
